import { createSharedComposable, useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { isMainMarket } from '@/core/lib/oddsHelper';
import { getTranslated } from '@/core/lib/translationHelper';
import { getScorePredictionPromotionUrl } from '@/core/lib/url';
import type { IOdds } from '@/interface/IOdds';
import type { ITranslation } from '@/interface/ITranslation';
import { EnumLanguage, MarketType, PlayerType, SboCurrency, SiteStyle } from '@/interface/enum';
import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

// Source: Junelson / Jason, 2026-10-06 — the entry icon is for B2C players with these currencies only.
const PROMOTION_CURRENCIES = [SboCurrency.TB, SboCurrency.PP, SboCurrency.VND, SboCurrency.RM].map(currency => currency.toUpperCase());
// How often the start/end window is re-checked while the page stays open.
const PERIOD_CHECK_INTERVAL_MS = 30 * 1000;

const normalize = (name: string) => name.trim().toUpperCase();

/**
 * Score Prediction promotion entry icon (TCSP-8392, Desktop Asia).
 * Shared by every league title, Correct Score title and badge on the page, so the stores,
 * computeds and the period clock exist once.
 * GA/GTM view/click tracking lives on the badge (ScorePredictionPromotionBadge.vue).
 */
export const useScorePredictionPromotion = createSharedComposable(() => {
    const now = useNow({ interval: PERIOD_CHECK_INTERVAL_MS });
    const { playerType, sboCurrency } = storeToRefs(useCustomerStore());
    const { scorePredictionLeagueNames, scorePredictionPromotionPeriod } = storeToRefs(useToggleStore(SiteStyle.Asia));
    const { isMixParlay } = storeToRefs(useAsiaSportMarketInfoStore());

    const eventPageUrl = getScorePredictionPromotionUrl();
    const promotionLeagueNames = computed(() => scorePredictionLeagueNames.value.map(normalize));

    const isEligiblePlayer = computed(() => playerType.value === PlayerType.B2C
        && PROMOTION_CURRENCIES.includes((sboCurrency.value ?? '').toUpperCase()));

    const isInPeriod = computed(() => {
        const period = scorePredictionPromotionPeriod.value;
        return !!period && period.start <= now.value && now.value < period.end;
    });

    const isActive = computed(() => isEligiblePlayer.value
        && isInPeriod.value
        && !isMixParlay.value);

    function isPromotionLeague(leagueName: ITranslation[]): boolean {
        return isActive.value && promotionLeagueNames.value.includes(normalize(getTranslated(leagueName, EnumLanguage.EN)));
    }

    function isPromotionMarket(leagueName: ITranslation[], odds: IOdds): boolean {
        return odds.marketType === MarketType.CorrectScore
            && isMainMarket(odds)
            && isPromotionLeague(leagueName);
    }

    function openEventPage(): void {
        globalThis.location.href = eventPageUrl;
    }

    return { isPromotionLeague, isPromotionMarket, openEventPage };
});
